"use client";

import { LEAD_DROP_VARIANTS } from "@/constants/leadConstants";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { ProgramCardMetadata } from "@/types/programCard";
import Image from "next/image";
import React from "react";
import { useDispatch } from "react-redux";
import { Button } from "../atoms/Button";

export interface CourseCardProps {
  data: ProgramCardMetadata;
  card_background_color: string;
  onBrochureDownload?: () => void;
  onKnowMore?: () => void;
  onCardClick?: () => void;
  className?: string;
  blurClassName?: string;
  /**
   * Atlas blog / narrow column (Figma): thumbnail + title stack in a horizontal row;
   * full-width hero image layout is used when false.
   */
  railLayout?: boolean;
}

const CourseCard: React.FC<CourseCardProps> = ({
  data,
  card_background_color,
  onBrochureDownload,
  onKnowMore,
  onCardClick,
  className,
  blurClassName,
  railLayout = false,
}) => {
  const dispatch = useDispatch();
  // Stop propagation on button clicks so they don't trigger card click
  const handleBrochureClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.DOWNLOAD,
        downloadLink: data?.meta_brochure_url,
      }),
    );
  };

  const handleKnowMoreClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onKnowMore?.();
  };

  const titleBlock = (
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <span
        className={cn(
          "line-clamp-1 text-[12px] font-normal leading-normal",
          !railLayout && "md:text-[14px] lg:text-[18px]",
        )}
        style={{ color: data?.meta_subtext_color }}
      >
        {data?.meta_subtext}
      </span>

      <p
        className={cn(
          "line-clamp-1 text-[18px] font-bold leading-snug",
          railLayout && "sm:text-[18px] sm:font-semibold",
          !railLayout &&
            "md:text-[18px] md:font-semibold lg:text-[22px] lg:font-semibold xl:text-[26px] xl:font-bold",
        )}
        style={{ color: data?.meta_course_name_text_color }}
      >
        {data?.meta_full_course_name}
      </p>

      <p
        className={cn(
          "line-clamp-2 text-[14px] font-normal leading-normal",
          railLayout ? "min-h-0" : "h-[42px] md:h-[42px] lg:h-[52px] xl:h-[56px]",
          !railLayout && "lg:text-[16px] xl:text-[18px]",
        )}
        style={{ color: data?.meta_short_course_name_text_color }}
      >
        {data?.meta_short_course_name}
      </p>
    </div>
  );

  const statsAndActions = (
    <div className={blurClassName}>
      <div
        className={cn(
          "flex flex-col backdrop-blur-[1px] transition-all duration-300",
        )}
      >
        <div
          className={cn("grid grid-cols-2 gap-2", railLayout && "gap-3 sm:gap-4")}
        >
          <div className="flex min-w-0 items-center gap-1">
            {data?.meta_enroll_icon && (
              <div
                className={cn(
                  "relative h-[24px] w-[24px] shrink-0",
                  "xl:h-[26px] xl:w-[26px]",
                )}
              >
                <Image
                  src={data?.meta_enroll_icon}
                  alt={data?.meta_enroll_icon_alt || "enroll icon"}
                  fill
                  className="object-contain"
                />
              </div>
            )}
            <span
              className={cn(
                "min-w-0 truncate text-[12px] font-semibold leading-normal",
                "md:text-[14px]",
                "lg:text-[14px]",
                "xl:text-[18px]",
              )}
              style={{ color: data?.meta_enroll_text_color }}
            >
              {data?.meta_enroll_text}
            </span>
          </div>
          <div className="flex min-w-0 items-center justify-end gap-1">
            {data?.meta_money_icon && (
              <div
                className={cn(
                  "relative h-[24px] w-[24px] shrink-0",
                  "xl:h-[26px] xl:w-[26px]",
                )}
              >
                <Image
                  src={data?.meta_money_icon}
                  alt={data?.meta_money_icon_alt || "money icon"}
                  fill
                  className="object-contain"
                />
              </div>
            )}
            <span
              className={cn(
                "min-w-0 truncate text-right text-[12px] font-semibold leading-normal",
                "md:text-[14px]",
                "lg:text-[14px]",
                "xl:text-[18px]",
              )}
              style={{
                color:
                  data?.meta_money_text_color ||
                  data?.meta_money_icon_color,
              }}
            >
              {data?.meta_money_text || data?.meta_subtext}
            </span>
          </div>
        </div>
        <div
          className={cn(
            "my-spacing-8 h-0.5 w-full rounded-full bg-gray-2",
            railLayout && "my-4",
          )}
        />
      </div>

      <div
        className={cn(
          railLayout
            ? "flex justify-between gap-2 sm:gap-3 [&>button]:min-w-0 [&>button]:shrink"
            : "grid grid-cols-2 gap-2",
        )}
      >
        {data?.meta_enable_brochure && data?.meta_brochure_url && (
          <Button
            classType={"outline"}
            onClick={handleBrochureClick}
            withDownload={!!data?.meta_brochure_icon}
            downloadIcon={
              data?.meta_brochure_icon && (
                <Image
                  src={data?.meta_brochure_icon}
                  alt={data?.meta_brochure_icon_alt || "brochure icon"}
                  width={railLayout ? 18 : 24}
                  height={railLayout ? 18 : 24}
                />
              )
            }
            className={cn(
              "flex-1 px-spacing-8 py-spacing-6 font-semibold leading-normal",
              "text-[14px] sm:gap-3",
              "md:text-[16px]",
              "lg:text-[18px]",
              "xl:text-[24px]",
              railLayout &&
                "justify-start rounded-lg !px-2 py-2 text-[14px] sm:text-[15px]",
            )}
            style={{
              borderColor:
                data?.meta_brochure_button_background_color ||
                data?.meta_brochure_text_color,
            }}
          >
            <span style={{ color: data?.meta_brochure_text_color }}>
              {data?.meta_brochure_text}
            </span>
          </Button>
        )}

        <Button
          classType={
            railLayout
              ? (data?.meta_cta_button_class_type as any) || "ghost"
              : (data?.meta_cta_button_class_type as any) || "default"
          }
          onClick={handleKnowMoreClick}
          withArrow={
            railLayout
              ? Boolean(
                  data?.meta_cta_mobile_icon ||
                    data?.meta_cta_button_icon ||
                    ((data?.meta_cta_button_class_type as string) || "ghost") ===
                      "ghost",
                )
              : !!data?.meta_cta_button_icon
          }
          arrowIcon={
            railLayout
              ? data?.meta_cta_mobile_icon || data?.meta_cta_button_icon ? (
                  <span className="inline-flex size-[18px] shrink-0 items-center justify-center self-center">
                    <Image
                      src={
                        (data?.meta_cta_mobile_icon ||
                          data?.meta_cta_button_icon) as string
                      }
                      alt={
                        data?.meta_cta_mobile_icon_alt ||
                        data?.meta_cta_button_icon_alt ||
                        "cta button icon"
                      }
                      width={18}
                      height={18}
                      className="object-contain"
                    />
                  </span>
                ) : undefined
              : data?.meta_cta_button_icon ? (
                  <Image
                    src={data.meta_cta_button_icon}
                    alt={data?.meta_cta_button_icon_alt || "cta button icon"}
                    width={24}
                    height={24}
                  />
                ) : undefined
          }
          arrowVariant={
            (data?.meta_cta_button_arrow_variant as any) || "secondary"
          }
          arrowFill={
            railLayout
              ? (data?.meta_cta_mobile_text_color ||
                  data?.meta_cta_button_text_color ||
                  "#BE2330")
              : undefined
          }
          className={cn(
            "flex-1 px-spacing-8 py-spacing-6 font-semibold leading-normal",
            "text-[14px] sm:gap-3",
            "md:text-[16px]",
            "lg:text-[18px]",
            "xl:text-[24px]",
            railLayout &&
              cn(
                "!items-center !gap-1 !px-2 !py-2 text-[14px] sm:!gap-1.5 sm:text-[15px] transition-colors hover:opacity-80",
                (data?.meta_cta_mobile_icon ||
                  data?.meta_cta_button_icon ||
                  ((data?.meta_cta_button_class_type as string) || "ghost") ===
                    "ghost")
                  ? "!justify-end"
                  : "justify-center",
              ),
          )}
          style={{
            backgroundColor: railLayout
              ? data?.meta_cta_mobile_background_color ||
                data?.meta_cta_button_background_color
              : data?.meta_cta_button_background_color,
          }}
        >
          <span
            className={cn(railLayout && "min-w-0 truncate")}
            style={{
              color: railLayout
                ? data?.meta_cta_mobile_text_color ||
                  data?.meta_cta_button_text_color
                : data?.meta_cta_button_text_color,
            }}
          >
            {data?.meta_cta_button_text}
          </span>
        </Button>
      </div>
    </div>
  );

  return (
    <div
      className={cn(
        railLayout
          ? cn(
              "flex w-full min-w-0 max-w-full flex-col rounded-lg border border-gray-3 p-3 shadow-sm transition-all duration-300 sm:p-4",
              onCardClick && "cursor-pointer hover:shadow-lg",
            )
          : cn(
              "flex w-[292px] flex-col rounded-lg border border-gray-3 p-spacing-12 transition-all duration-300",
              // Definite width: the grid centres itself with intrinsic columns, and a
              // percentage width against those would resolve as `auto` (content-sized).
              "sm:w-[312px] sm:px-spacing-16",
              "md:w-[312px] md:p-spacing-16",
              "lg:w-[344px] lg:p-spacing-18",
              "xl:w-[482px] xl:p-spacing-18",
              onCardClick && "cursor-pointer hover:shadow-lg",
            ),
        className,
      )}
      style={{ backgroundColor: card_background_color }}
      onClick={onCardClick}
    >
      {railLayout ? (
        <>
          <div className="mb-4 flex min-w-0 flex-row items-start gap-3 sm:mb-5 sm:gap-4">
            <div className="relative h-[64px] w-[74px] shrink-0 overflow-hidden rounded-lg sm:h-[68px] sm:w-[80px]">
              {data?.meta_thumbnail_image && (
                <Image
                  src={data?.meta_thumbnail_image || ""}
                  alt={
                    data?.meta_thumbnail_image_alt ||
                    data?.meta_full_course_name ||
                    "course"
                  }
                  fill
                  className="object-cover"
                />
              )}
            </div>
            {titleBlock}
          </div>
          {statsAndActions}
        </>
      ) : (
        <div className="flex flex-col gap-spacing-12">
          <div
            className={cn(
              "relative h-[82px] w-full overflow-hidden rounded-lg",
              "sm:h-[82px]",
              "lg:h-[108px]",
              "xl:h-[132px]",
            )}
          >
            {data?.meta_thumbnail_image && (
              <Image
                src={data?.meta_thumbnail_image || ""}
                alt={
                  data?.meta_thumbnail_image_alt ||
                  data?.meta_full_course_name ||
                  "course"
                }
                fill
                className="object-cover"
              />
            )}
          </div>

          <div
            className={cn("flex flex-col gap-spacing-8 ", "md:gap-spacing-24")}
          >
            {titleBlock}
            {statsAndActions}
          </div>
        </div>
      )}
    </div>
  );
};
export default CourseCard;
